// src/utils/pageContext.ts
//
// Derives STRUCTURED page context from the current route for the platform
// assistant (plan 2026-07-28 §5, step 3.2). The assistant sends this so
// "this source" / "this segment" resolves without the user naming it.
//
// SECURITY / CONTRACT (mirrored on the backend):
//   * Structured fields ONLY — never a raw URL or free text.
//   * `module` / `submenu` are registry slugs; the backend re-validates them
//     against its menu-registry mirror and drops anything it doesn't know, so
//     we only ever emit slugs the shared MENU_REGISTRY recognises.
//   * `entity_id` is an UNTRUSTED HINT extracted from the URL for the entity
//     detail routes only (managed source, segment, report). The backend resolves it
//     with an account-scoped query before injecting anything; a tool never acts
//     on the raw id. We only emit it when it looks like the expected public id,
//     which also keeps `/new`, `/discovery`, `/setup` out of the entity slot.
//
// Pure function → unit-tested in pageContext.test.ts.
import { MENU_REGISTRY } from "@/constants/menuRegistry";
import { stripTenantPrefix } from "@/routing/tenantPaths";

export interface PageContext {
  module?: string;
  submenu?: string;
  entity_id?: string;
}

// Public-id-safe charset — matches the backend PageContext schema pattern.
const PUBLIC_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

// {module: Set<submenu>} built from the shared registry — the single source of
// truth for which slugs are valid, so this never drifts from the nav.
const SUBMENUS: Record<string, Set<string>> = Object.fromEntries(
  MENU_REGISTRY.map((s) => [s.key, new Set(s.submenus.map((x) => x.key))]),
);

/** A managed-source detail id: DataSource (`ds_`) or pipeline config (`mpc_`). */
function managedSourceId(v: string | undefined): string | undefined {
  return v && (v.startsWith("ds_") || v.startsWith("mpc_")) && PUBLIC_ID_RE.test(v)
    ? v
    : undefined;
}

/** A segment detail id (`seg_`). Excludes `new` / reserved path segments. */
function segmentId(v: string | undefined): string | undefined {
  return v && v.startsWith("seg_") && PUBLIC_ID_RE.test(v) ? v : undefined;
}

/** A report detail id (`rpt_`). Excludes `review-queue` and other reserved segments. */
function reportId(v: string | undefined): string | undefined {
  return v && v.startsWith("rpt_") && PUBLIC_ID_RE.test(v) ? v : undefined;
}

/**
 * Map a pathname to `{module, submenu, entity_id}`, or `undefined` when the
 * route carries no useful, registry-known context (e.g. `/`, `/docs`).
 */
export function derivePageContext(pathname: string): PageContext | undefined {
  // Tenant pages live under `/a/<account>/…` (ADR-0302); the section starts
  // after that prefix. Without the strip every tenant page would read as the
  // unknown section "a" and the assistant would silently lose its context.
  const path = stripTenantPrefix(pathname.split("?")[0].split("#")[0]).replace(/\/+$/, "");
  const seg = path.split("/").filter(Boolean);
  if (seg.length === 0) return undefined;

  const [module, sub, third] = seg;

  // Reports: the routes are top-level (`/reports/...`) but the nav entry is
  // the "reports" submenu of "insights". This mapping MUST come before the
  // registry guard below, which would otherwise drop "reports" as an unknown
  // section (plan 2026-08-23 assistant-report-authoring, step 5).
  //   /reports                    -> Insights → Reports, no entity
  //   /reports/review-queue       -> Insights → Reports, no entity (reserved)
  //   /reports/rpt_x[/fullscreen|/diff] -> ... + entity_id
  // A second segment that is not a valid `rpt_` public id (`review-queue`,
  // a typo'd prefix) keeps the menu path and drops the entity — same shape as
  // the ds_/mpc_/seg_ cases below.
  if (module === "reports") {
    const entity_id = reportId(sub);
    return {
      module: "insights",
      submenu: "reports",
      ...(entity_id ? { entity_id } : {}),
    };
  }

  // Only sections the shared registry knows about produce context.
  if (!(module in SUBMENUS)) return undefined;

  // --- v1 entity detail routes -------------------------------------------
  // Managed source: /data-management/{sources|flows}/:id  (id = ds_ | mpc_)
  if (module === "data-management" && (sub === "sources" || sub === "flows")) {
    const entity_id = managedSourceId(third);
    return { module, submenu: sub, ...(entity_id ? { entity_id } : {}) };
  }

  // Segment: /activation/segments/:id  (id = seg_); NOT /new or /discovery.
  if (module === "activation" && sub === "segments") {
    if (third === "discovery") return { module, submenu: "discovery" };
    const entity_id = segmentId(third);
    return { module, submenu: "segments", ...(entity_id ? { entity_id } : {}) };
  }

  // --- everything else: module + a registry-valid submenu, or module only ---
  const submenu = sub && SUBMENUS[module].has(sub) ? sub : undefined;

  // Automation detail/new pages (/automations, /automations/new,
  // /automations/:id) live under the "monitors" submenu; /automations/alerts
  // is the only path whose second segment is a real submenu slug.
  if (module === "automations" && !submenu) {
    return { module, submenu: "monitors" };
  }

  // /insights (no second segment) is the Insights home page, whose registry
  // slug is "home" — same shape as the automations case above.
  if (module === "insights" && !submenu) {
    return { module, submenu: "home" };
  }

  return submenu ? { module, submenu } : { module };
}
